import React, { useState } from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockContainer from '../BlockContainer';

import css from './BlockAccordion.module.css';

const FieldMedia = props => {
  const { className, media, sizes, options } = props;
  const hasMediaField = hasDataInFields([media], options);
  return hasMediaField ? (
    <div className={classNames(className, css.media)}>
      <Field data={media} sizes={sizes} options={options} />
    </div>
  ) : null;
};

/**
 * Accordion block component for FAQ-style content.
 * The title (h3) acts as a clickable trigger to reveal/hide the content.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.blockId id from the block config
 * @param {string} props.blockName name from the block config (not used)
 * @param {'defaultBlock'} props.blockType blockType is set to 'defaultBlock'
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.mediaClassName add styles for the block's attached media field
 * @param {string?} props.textClassName add styles for the block's attached text field
 * @param {string?} props.ctaButtonClass add styles for the block's attached CTA field
 * @param {Object?} props.title heading config for the block (acts as accordion trigger)
 * @param {Object?} props.text content config for the block (accordion content)
 * @param {Object?} props.callToAction call to action button
 * @param {string?} props.responsiveImageSizes
 * @param {Object} props.options extra options for the block component
 * @returns {JSX.Element} accordion block component
 */
const BlockAccordion = props => {
  const {
    blockId,
    className,
    rootClassName,
    mediaClassName,
    textClassName,
    ctaButtonClass,
    title,
    text,
    callToAction,
    media,
    responsiveImageSizes,
    options,
    alignment,
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  const classes = classNames(rootClassName || css.root, className, {
    [css.isOpen]: isOpen,
  });
  const hasTextComponentFields = hasDataInFields([title, text, callToAction], options);

  const alignmentClasses = {
    left: css.alignLeft,
    center: css.alignCenter,
    right: css.alignRight,
  };

  const alignmentClass = alignmentClasses[alignment];

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  // Handle keyboard accessibility
  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleAccordion();
    }
  };

  return (
    <BlockContainer id={blockId} className={classes}>
      <FieldMedia
        media={media}
        sizes={responsiveImageSizes}
        className={mediaClassName}
        options={options}
      />
      {hasTextComponentFields ? (
        <div className={classNames(textClassName, alignmentClass, css.accordionWrapper)}>
          {/* Accordion Header (Title) */}
          <div
            className={css.accordionHeader}
            onClick={toggleAccordion}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-expanded={isOpen}
          >
            <Field data={title} options={options} />
            <span className={css.accordionIcon} aria-hidden="true">
              {isOpen ? '−' : '+'}
            </span>
          </div>

          {/* Accordion Content */}
          <div className={css.accordionContent} aria-hidden={!isOpen}>
            <div className={css.accordionContentInner}>
              <Field data={text} options={options} />
              <Field data={callToAction} className={ctaButtonClass} options={options} />
            </div>
          </div>
        </div>
      ) : null}
    </BlockContainer>
  );
};

export default BlockAccordion;

